import type React from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Chip,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import {
  CheckCircle as CheckIcon,
  CalendarToday as CalendarIcon,
  CreditCard as CreditCardIcon,
  AccessTime as AccessTimeIcon,
} from "@mui/icons-material";
import { useQuery } from "@tanstack/react-query";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { DataGrid, type GridColDef, GridToolbar } from "@mui/x-data-grid";
import { manageMembershipQueryOptions } from "../../queryOptions/ManageMembership";
import { useAuth } from "../../context/AuthContext";
import { calculateDaysRemaining } from "../../hooks/Date";
import type { Membership } from "../../schemas/memberships";

const MembershipsSection: React.FC = () => {
  const { userId } = useAuth();

  const { data, isLoading } = useQuery(
    manageMembershipQueryOptions(userId || "")
  );

  const activeMembership =
    !isLoading &&
    data &&
    data.membership.find(
      (membership: Membership) => membership.status === "active" || membership.status === "resumed"
    );

  // Prepare all memberships for the table
  const allMemberships = !isLoading && data ? data.membership : [];

  // Define columns for the DataGrid
  const columns: GridColDef[] = [
    {
      field: "plan",
      headerName: "Plan",
      width: 120,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight={500}>
          {params.row.membershipListing?.name || "-"}
        </Typography>
      ),
    },
    {
      field: "status",
      headerName: "Status",
      width: 120,
      renderCell: (params) => {
        const getStatusColor = (status: string) => {
          switch (status.toLowerCase()) {
            case "active":
              return "success";
            case "expired":
              return "error";
            case "cancelled":
              return "warning";
            case "pending":
              return "info";
            default:
              return "default";
          }
        };

        return (
          <Chip
            label={params.value === "pending" ? "Payment Pending" : params.value.charAt(0).toUpperCase() + params.value.slice(1)}
            color={getStatusColor(params.value) as any}
            size="small"
            variant={params.value === "active" ? "filled" : "outlined"}
          />
        );
      },
    },
    {
      field: "amount",
      headerName: "Amount",
      width: 120,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight={500}>
          ${params.row.amount || 0}
        </Typography>
      ),
    },
    {
      field: "startDate",
      headerName: "Start Date",
      width: 130,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.value ? new Date(params.value).toLocaleDateString() : "-"}
        </Typography>
      ),
    },
    {
      field: "endDate",
      headerName: "End Date",
      width: 130,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.value && params.row.cancelledAt ? new Date(params.value).toLocaleDateString() : "-"}
        </Typography>
      ),
    },
    {
      field: "pauseDate",
      headerName: "Pause Date",
      width: 130,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.value ? new Date(params.value).toLocaleDateString() : "-"}
        </Typography>
      ),
    },
    {
      field: "resumeDate",
      headerName: "Resume Date",
      width: 130,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.value ? new Date(params.value).toLocaleDateString() : "-"}
        </Typography>
      ),
    },
    {
      field: "cancelledAt",
      headerName: "Cancelled At",
      width: 140,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.value && params.row.cancelledAt
            ? new Date(params.value).toLocaleDateString()
            : "-"}
        </Typography>
      ),
    },
  ];

  // Transform membership data for DataGrid
  const tableRows = allMemberships.map((membership: Membership) => ({
    id: membership.id,
    plan: membership.membershipListing?.name,
    status: membership.status,
    amount: membership.amount,
    // interval: membership.membershipListing?.interval,
    startDate: membership.startDate,
    endDate: membership.endDate,
    cancelledAt: membership.cancelledAt,
    pauseDate: membership.pauseDate,
    resumeDate: membership.resumeDate,
    nextPaymentDate: membership.nextPaymentDate,
    // features: membership.membershipListing?.features || [],
    membershipListing: membership.membershipListing,
  }));

  const currentMembership = activeMembership
    ? {
        id: activeMembership.id,
        plan: activeMembership.membershipListing?.name || "-",
        amount: activeMembership.amount || 0,
        startDate: activeMembership.startDate
          ? new Date(activeMembership.startDate).toLocaleDateString()
          : "-",
        nextBillingDate: activeMembership.nextPaymentDate
          ? new Date(activeMembership.nextPaymentDate).toLocaleDateString()
          : "-",
          cancelledAt: activeMembership.cancelledAt
          ? new Date(activeMembership.cancelledAt).toLocaleDateString() : "-",
        interval: activeMembership.membershipListing?.interval || "-",
        features: activeMembership.membershipListing?.features || [],
        isActive: activeMembership.status === "active",
        discountPercent:
          typeof activeMembership.discountPercent === "number"
            ? activeMembership.discountPercent
            : activeMembership.discountPercent
              ? Number(activeMembership.discountPercent)
              : null,
        discountExpiryDate: activeMembership.discountExpiryDate
          ? new Date(activeMembership.discountExpiryDate)
          : null,
        regularPrice: activeMembership.membershipListing?.amount
          ? Number(activeMembership.membershipListing.amount)
          : 0,
      }
    : null

  const progressPercentage =
    !isLoading &&
    data &&
    currentMembership &&
    calculateDaysRemaining(
      currentMembership.startDate,
      currentMembership.nextBillingDate
    );

  const nextRenewalPrice =
    currentMembership &&
    currentMembership.discountPercent &&
    currentMembership.discountExpiryDate &&
    currentMembership.discountExpiryDate.getTime() >= Date.now()
      ? currentMembership.regularPrice *
        (1 - currentMembership.discountPercent / 100)
      : currentMembership?.regularPrice ?? null;

  return (
    <Box>
      <Typography variant="h5" component="h2" gutterBottom>
        Memberships
      </Typography>

      {/* Current Membership Status */}
      {currentMembership ? (
        <>
          <Paper elevation={3} sx={{ p: 3, mb: 4, borderRadius: 2 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Typography variant="h6">Current Membership</Typography>
              <Chip
                label="Active"
                color="success"
                size="small"
                sx={{ fontWeight: 500 }}
              />
            </Box>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <List dense>
                  <ListItem>
                    <ListItemIcon>
                      <CheckIcon color="success" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Plan"
                      secondary={currentMembership.plan}
                      primaryTypographyProps={{
                        color: "text.secondary",
                        variant: "body2",
                      }}
                      secondaryTypographyProps={{
                        color: "text.primary",
                        variant: "body1",
                        fontWeight: 500,
                      }}
                    />
                  </ListItem>

                  <ListItem>
                    <ListItemIcon>
                      <CalendarIcon color="action" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Start Date"
                      secondary={currentMembership.startDate}
                      primaryTypographyProps={{
                        color: "text.secondary",
                        variant: "body2",
                      }}
                      secondaryTypographyProps={{
                        color: "text.primary",
                        variant: "body1",
                      }}
                    />
                  </ListItem>

                  <ListItem>
                    <ListItemIcon>
                      <CreditCardIcon color="action" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Next Billing Date"
                      secondary={currentMembership.nextBillingDate}
                      primaryTypographyProps={{
                        color: "text.secondary",
                        variant: "body2",
                      }}
                      secondaryTypographyProps={{
                        color: "text.primary",
                        variant: "body1",
                      }}
                    />
                  </ListItem>

                  <ListItem>
                    <ListItemIcon>
                      <CreditCardIcon color="action" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Discount"
                      secondary={
                        currentMembership.discountPercent &&
                        currentMembership.discountExpiryDate
                          ? `${currentMembership.discountPercent}% off until ${currentMembership.discountExpiryDate.toLocaleDateString()}. After discount: $${currentMembership.regularPrice.toFixed(2)}`
                          : currentMembership.discountPercent
                            ? `${currentMembership.discountPercent}% off (valid-until unknown). After discount: $${currentMembership.regularPrice.toFixed(2)}`
                            : "No discount"
                      }
                      primaryTypographyProps={{
                        color: "text.secondary",
                        variant: "body2",
                      }}
                      secondaryTypographyProps={{
                        color: "text.primary",
                        variant: "body1",
                      }}
                    />
                  </ListItem>
                </List>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box
                  sx={{
                    p: 2,
                    bgcolor: "background.default",
                    borderRadius: 2,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 1,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Membership Period
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {progressPercentage}
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={Number.parseInt(progressPercentage)}
                    sx={{ height: 8, borderRadius: 4, mb: 1 }}
                  />
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <AccessTimeIcon fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">
                      Renews automatically on{" "}
                      {currentMembership.nextBillingDate}
                      {nextRenewalPrice !== null && nextRenewalPrice !== undefined
                        ? ` for $${nextRenewalPrice.toFixed(2)}`
                        : ""}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </>
      ) : (
        <Paper elevation={3} sx={{ p: 3, mb: 4, borderRadius: 2 }}>
          <Typography variant="body1" color="text.secondary">
            No active membership found.
          </Typography>
        </Paper>
      )}
      <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Typography variant="h6">All Memberships</Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <InfoOutlinedIcon fontSize="small" color="action" />
            <Typography variant="body2" color="text.secondary">
              Current and past memberships ({allMemberships.length} total)
            </Typography>
          </Box>
        </Box>

        <Box sx={{ height: "fit-content", width: "auto" }}>
          <DataGrid
            rows={tableRows}
            columns={columns}
            initialState={{
              pagination: {
                paginationModel: { pageSize: 10 },
              },
              sorting: {
                sortModel: [{ field: "startDate", sort: "desc" }],
              },
            }}
            getRowId={(row) => row.id}
            pageSizeOptions={[5, 10, 25]}
            density="standard"
            disableRowSelectionOnClick
            disableColumnFilter
            disableDensitySelector
            slots={{ toolbar: GridToolbar }}
            slotProps={{
              toolbar: {
                showQuickFilter: true,
                quickFilterProps: { debounceMs: 500 },
              },
            }}
            sx={{
              border: "none",
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "#f5f5f5",
                fontWeight: 600,
              },
              "& .MuiDataGrid-row:hover": {
                backgroundColor: "#f8f9fa",
              },
              "& .MuiDataGrid-cell": {
                borderBottom: "1px solid #e0e0e0",
              },
              "& .MuiDataGrid-row": {
                "&:nth-of-type(even)": {
                  backgroundColor: "#fafafa",
                },
              },
              "& .MuiDataGrid-row[data-rowindex='0']": {
                backgroundColor: activeMembership ? "#e8f5e8" : "inherit",
                "&:hover": {
                  backgroundColor: activeMembership ? "#d4edda" : "#f8f9fa",
                },
              },
            }}
          />
        </Box>
      </Paper>
    </Box>
  );
};

export default MembershipsSection;
